import { Component } from '@angular/core';
import { SelectionService } from '../../../models/review/selection-service';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ProcessingQueueRequest } from 'src/app/models/processing-queue-request.model';
import { ProcessingQueueService } from 'src/app/services/processing-queue.service';
import { StepIds, Steps } from './steps';
import { BulkDocAddPayload } from '../../../models/review/bulk-doc-add-payload.model';
import { OkCancelDialogComponent } from 'src/app/components/dialogs/ok-cancel/ok-cancel-dialog.component';
import { Role } from 'src/app/models/role.model';
import { UserConfigsService } from 'src/app/services/user-configs.service';
import { Prompt } from '../../../models/prompt.model';
import { MultiDocField, MultiDocGroup } from '../../../models/field/multi-doc-field.model';
import { PromptManager } from '../../../managers/prompt.manager';
import { CodingFieldsService } from '../../../services/coding-fields.service';
import { Observable, map } from 'rxjs';
import { ThirdPartySettings } from '@store/third-party/third-party.state';
import { currentOpenAiSettings } from '@store/third-party/open-ai-settings/open-ai-settings.selector';
import { Store } from '@ngrx/store';
import { ThirdPartyId } from '@models/third-party-id.enum';
import { ClaudeModel } from '@models/amazon-bedrock-claude.model';
import { OpenAIModel } from '@models/open-ai-gpt.model';
import { AutoPilotClient, BulkDocAddRequest, ProcessingQueueRequest as ApiProcessingQueueRequest, MultiProcessingStepBody } from '@clients/api.client';

@Component({
	selector: 'lex-processing',
	templateUrl: './processing.component.html',
	styleUrls: ['./processing.component.scss'],
	standalone: false
})
export class ProcessingComponent {
	public steps = Steps.steps;
	public selectedStep;

	public selectedEngine = 'AmazonEngine';
	public selectedTranslationLanguage = 'af';

	public speakers = '0';
	public selectedTranscriptionLanguage = 'en-US';

	public prompts: Prompt[] = [];
	public selectedEngineModel: number = 0;
	public aiEngineModels = [
		{ value: 0, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT3_5Turbo, name: 'GPT-3.5 Turbo' },
		{ value: 1, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4, name: 'GPT-4' },
		{ value: 2, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4TurboPreview, name: 'GPT-4 Turbo (Preview)' },
		{ value: 3, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4Turbo, name: 'GPT-4 Turbo' },
		{ value: 4, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4Omni, name: 'GPT-4o' },
		{ value: 5, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4OmniMini, name: 'GPT-4o Mini' },
		{ value: 6, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4_1, name: 'GPT-4.1' },
		{ value: 7, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4_1Nano, name: 'GPT-4.1 Nano' },
		{ value: 8, id: ThirdPartyId.OpenAi, model: OpenAIModel.GPT4_1Mini, name: 'GPT-4.1 Mini' },
		{ value: 9, id: ThirdPartyId.OpenAi, model: OpenAIModel.o1, name: 'o1' },
		{ value: 10, id: ThirdPartyId.OpenAi, model: OpenAIModel.o3Mini, name: 'o3-mini' },
		{ value: 11, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_Sonnet, name: 'Claude 3 Sonnet' },
		{ value: 12, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_Sonnet, name: 'Claude 3.5 Sonnet' },
		{ value: 13, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_5_SonnetV2, name: 'Claude 3.5 Sonnet v2' },
		{ value: 14, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude3_7_Sonnet, name: 'Claude 3.7 Sonnet' },
		{ value: 15, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude4Sonnet, name: 'Claude 4 Sonnet' },
		{ value: 16, id: ThirdPartyId.AmazonBedrockClaude, model: ClaudeModel.Claude4Opus, name: 'Claude 4 Opus' }
	];
	public selectedPromptId: string = null;
	public selectedCodingFieldId: string = '';

	public codingFieldGroups: MultiDocGroup[] = [];

	private openAiSettings$: Observable<ThirdPartySettings> = this.store.select(currentOpenAiSettings());

	Role = Role;

	constructor(
		public selectionService: SelectionService,
		public ucs: UserConfigsService,
		private processingService: ProcessingQueueService,
		private dialog: MatDialog,
		private promptManager: PromptManager,
		private codingFieldsService: CodingFieldsService,
		private autoPilotClient: AutoPilotClient,
		private store: Store) {
		this.promptManager.getPrompts().subscribe((data) => {
			this.prompts = data;
			this.selectedPromptId = data[0].promptId;
		});
		this.openAiSettings$.subscribe(
			(settings) => {
				if (settings.isLinked) {
					this.selectedEngineModel = 0;
				}
				else {
					this.aiEngineModels = this.aiEngineModels.filter(x => x.id === ThirdPartyId.AmazonBedrockClaude);
					this.selectedEngineModel = 11;
				}
			});
		this.loadMultiDocGroupFields();

	}

	public stepChanged(step: MatSelectChange) {
		if (step.value === StepIds.AnswerResponsivenessPrompt && !this.selectedCodingFieldId) {
			this.selectionService.operationAlert = `No Result Field Selected for Answer Responsiveness Prompt`;
			this.selectedStep = null;
			return;
		}

		this.dialog.open(OkCancelDialogComponent, {
			data: {
				message: 'Add Doc Step To Processing?'
			}
		}).afterClosed().subscribe((result) => {
			if (!result) {
				this.selectedStep = null;
				return;
			}

			let functionArgument;
			if (step.value === StepIds.TranslateDoc) {
				functionArgument = {
					TranslateDoc: {
						TranslationEngine: this.selectedEngine,
						FromLanguageCode: this.selectedTranslationLanguage
					}
				};
			} else if (step.value === StepIds.TranscribeDoc) {
				functionArgument = {
					TranscribeDoc: {
						IsIdentifySpeakers: this.speakers !== '0',
						MaxSpeakers: this.speakers,
						FromLanguageCode: this.selectedTranscriptionLanguage
					}
				};
			} else if (step.value === StepIds.AnswerResponsivenessPrompt) {
				functionArgument = {
					userId: this.ucs.currentConfig.userId,
					promptId: this.selectedPromptId,
					codingFieldId: this.selectedCodingFieldId,
					externalAppId: this.aiEngineModels.find(x => x.value === this.selectedEngineModel)?.id,
					aiModel: this.aiEngineModels.find(x => x.value === this.selectedEngineModel)?.model
				};
				const queueRequest = new ApiProcessingQueueRequest;
				queueRequest.functionId = step.value;
				queueRequest.functionArgument = JSON.stringify(functionArgument);
				const bulkDocRequest = new BulkDocAddRequest;
				bulkDocRequest.docIds = this.selectionService.selection.selected.map(s => s.docId);
				bulkDocRequest.filterId = this.selectionService.getSavedFilterId();
				bulkDocRequest.selectAll = this.selectionService.selectAllFlag;
				const body = new MultiProcessingStepBody;
				body.request = queueRequest;
				body.bulkData = bulkDocRequest;
				this.autoPilotClient.queueProcessingAIRequest$(body).subscribe(() => {
					this.selectionService.operationAlert = `Docs added to Processing: ${Steps.steps.get(step.value)}`;
				});
				this.selectedStep = null;
				return;
			}

			const request: ProcessingQueueRequest = {
				functionId: step.value,
				functionArgument: JSON.stringify(functionArgument),
				docId: null
			};

			const bulk: BulkDocAddPayload = {
				docIds: this.selectionService.selection.selected.map(s => s.docId),
				filterId: this.selectionService.getSavedFilterId(),
				selectAll: this.selectionService.selectAllFlag
			};

			const body = {
				Request: request,
				BulkData: bulk
			};

			this.processingService.addMultiProcessingStep(body).subscribe();

			this.selectionService.operationAlert = `Docs added to Processing: ${Steps.steps.get(step.value)}`;

			this.selectedStep = null;
		});
	}

	public engineChanged(engine: MatSelectChange) {
		switch (engine.value) {
			case 'AmazonEngine':
				this.selectedTranslationLanguage = 'af'; // Afrikaans
				break;
			case 'GoogleEngine':
				this.selectedTranslationLanguage = ''; // Auto Detect
				break;
			default:
				console.warn('Unknown engine selection: ' + engine.value);
				break;
		}
	}

	private loadMultiDocGroupFields() {
		this.codingFieldsService.getFields(false).pipe(
			map((data) => {
				const sections = data.value.filter(x => x.fieldId === x.parentFieldId);
				data.value
					.filter(x => x.fieldId !== x.parentFieldId && x.type === 1)
					.forEach((x) => {
						const field = {} as MultiDocField;
						field.displayTitle = x.displayTitle;
						field.fieldId = x.fieldId;
						field.rank = x.rank;
						field.fieldType = x.type;
						const parentField = sections.find((y) => y.fieldId === x.parentFieldId);
						if (this.codingFieldGroups.some(x => x.displayTitle === parentField.displayTitle)) {
							const group = this.codingFieldGroups.find(x => x.displayTitle === parentField.displayTitle);
							group.fields.push(field);
						}
						else {
							const group = {} as MultiDocGroup;
							group.displayTitle = parentField.displayTitle;
							group.fields = [];
							group.fields.push(field);
							this.codingFieldGroups.push(group);
						}
					});
			})).subscribe();
	}
}
